import { useEffect, useState } from "react";
import {
  ChallengeRefContent,
  MarkdownContent,
  Numerical,
  Page,
  Question,
  Quiz,
  QuizRefContent,
  Slide,
} from "../../types";
import { Answer } from "@quark/shared-types";
import {
  getChallengeRef,
  getKnowledge,
  getPage,
  getQuizRef,
  submitUserAnswer,
} from "../../api";
import _ from "lodash";

const usePage = (courseId: Numerical, pageId: Numerical) => {
  const [page, setPage] = useState<Page>();
  const [slides, setSlides] = useState<Slide[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!page) {
      return;
    }

    const promises = page.components
      .map((component) => {
        if (component.type === "QuizRef") {
          const content = component.content as QuizRefContent;
          return getQuizRef(content.questionId);
        }
        if (component.type === "Markdown") {
          const content = component.content as MarkdownContent;
          return getKnowledge(content.knowledgeId);
        }
        if (component.type === "ChallengeRef") {
          const content = component.content as ChallengeRefContent;
          return getChallengeRef(content.challengeId).then((data: any) => {
            return {
              id: data.challenge_id,
              title: data.title,
              variant: "challenge",
              challenge: data,
            } as Quiz;
          });
        }
        return null;
      })
      .filter((p) => !!p);
    Promise.all(promises).then((quizList) => {
      const slides: Slide[] = [];
      quizList.forEach((quiz) => {
        if (!quiz) {
          return;
        }

        if (quiz.variant === "quiz") {
          if (quiz.knowledges?.length) {
            slides.push({
              id: quiz.id,
              title: quiz.title,
              type: "quiz-knowledge",
              data: quiz.knowledges,
            });
          }
          if (quiz.question) {
            slides.push({
              id: quiz.id,
              title: quiz.title,
              type: "quiz-question",
              data: quiz.question,
            });
          }
        }

        if (quiz.variant === "challenge") {
          if (quiz.challenge) {
            slides.push({
              id: quiz.id,
              title: quiz.title,
              type: "edu-challenge",
              data: quiz.challenge,
            });
          }
        }

        if (quiz.variant === "knowledge") {
          if (quiz.knowledges?.length) {
            slides.push({
              id: quiz.id,
              title: quiz.title,
              type: "edu-knowledge",
              data: quiz.knowledges,
            });
          }
        }
      });
      setSlides(slides);
      setLoading(false);
    });
  }, [page]);

  useEffect(() => {
    setLoading(true);
    setSlides([]);
    getPage(courseId, pageId).then((page) => {
      setPage(page);
    });
  }, [courseId, pageId]);

  const handleSubmitAnswer = (id: Numerical, data: Answer[]) => {
    // 如果答案错误，则不提交

    setLoading(false);
    const slide = slides.find((s) => s.id === id && s.type === "quiz-question");
    if (!slide) {
      setLoading(false);
      return;
    }

    const quizAnswerTexts = (slide.data as Question).quizAnswer.map(
      (a) => a.text
    );

    const userAnswerTexts = data.map((d) => d.text);

    let isAnswerRight;
    if ((slide.data as Question).type === "mult_choice") {
      isAnswerRight = _.isEqual(
        [...quizAnswerTexts].sort(),
        [...userAnswerTexts].sort()
      );
    } else {
      isAnswerRight = _.isEqual(quizAnswerTexts, userAnswerTexts);
    }

    if (isAnswerRight) {
      submitUserAnswer(id, data)
        .then((result: Record<string, any>) => {
          setSlides(
            slides.map((slide) => {
              if (slide.type === "quiz-question" && slide.id === id) {
                return {
                  ...slide,
                  data: {
                    ...slide.data,
                    isRight: result.is_right,
                    userAnswer: data,
                  },
                };
              }
              return slide;
            })
          );
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      setSlides(
        slides.map((slide) => {
          if (slide.type === "quiz-question" && slide.id === id) {
            return {
              ...slide,
              data: {
                ...slide.data,
                isRight: false,
                userAnswer: data,
              },
            };
          }
          return slide;
        })
      );
    }
  };

  return { page, loading, slides, handleSubmitAnswer };
};

export default usePage;
